import type { CityConfig } from "../city-template/types";

export const sanBernardinoConfig: CityConfig = {
  city: "San Bernardino",
  state: "California",
  stateAbbr: "CA",
  slug: "san-bernardino",
  route: "/san-bernardino",
  title: "Emergency Passport San Bernardino, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in San Bernardino, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for San Bernardino County and Inland Empire travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in San Bernardino, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help San Bernardino and Inland Empire travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=San%20Bernardino%20California%20cityscape%20at%20golden%20hour%20with%20downtown%20skyline%20and%20historic%20courthouse%2C%20San%20Bernardino%20Mountains%20visible%20in%20the%20background%20with%20snow-capped%20peaks%2C%20warm%20sunset%20light%20over%20the%20Inland%20Empire%20landscape%2C%20modern%20suburban%20buildings%20with%20desert%20landscaping%2C%20clean%20urban%20environment%2C%20professional%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20modern%20desert%20city%20atmosphere&width=1800&height=700&seq=san-bernardino-hero-001&orientation=landscape",
  heroImageAlt: "San Bernardino California skyline at golden hour with downtown and mountain backdrop",
  geoPosition: "34.1083;-117.2898",
  geoPlacename: "San Bernardino, California",
  lastModified: "2026-07-21",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "11000 Wilshire Blvd Suite 1000, Los Angeles, CA 90024",
    distance: "Located in West LA — approximately 65–80 minutes from San Bernardino via I-10",
  },
  nearbyCitiesZips: [
    { name: "San Bernardino", zips: "92401–92411, 92415" },
    { name: "Rialto", zips: "92376, 92377" },
    { name: "Colton", zips: "92313, 92324" },
    { name: "Highland", zips: "92346, 92386" },
    { name: "Redlands", zips: "92373, 92374, 92375" },
    { name: "Fontana", zips: "92335, 92336, 92337" },
    { name: "Rancho Cucamonga", zips: "91701, 91730, 91737, 91739" },
    { name: "Ontario", zips: "91761, 91762, 91764" },
    { name: "Riverside", zips: "92501–92509, 92512–92519, 92521" },
    { name: "Moreno Valley", zips: "92551, 92553, 92555, 92557" },
    { name: "Loma Linda", zips: "92350, 92354" },
    { name: "Yucaipa", zips: "92399" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Inland Empire departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Los Angeles", route: "/los-angeles" },
    { name: "Riverside", route: "/riverside" },
    { name: "San Diego", route: "/san-diego" },
    { name: "Anaheim", route: "/anaheim" },
    { name: "Pasadena", route: "/pasadena" },
    { name: "Santa Ana", route: "/santa-ana" },
    { name: "Long Beach", route: "/long-beach" },
  ],
  fpcOfficeNote: "Fast Passport Center serves San Bernardino remotely via secure courier. We do not have a walk-in office in San Bernardino — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Inland Empire travelers.",
  fpcOfficeAddress: "355 S Grand Ave, Ste 2450, Los Angeles, CA 90071",
  fpcOfficeDistance: "Serving all of San Bernardino County",
  serviceAreaIntro: "From downtown San Bernardino to the foothills of the San Bernardino Mountains, from the Cajon Pass to the border of Riverside County — Fast Passport Center serves the entire San Bernardino County community. We regularly help travelers from Rialto, Colton, Redlands, Fontana, and Highland get their passports fast.",
  nearbyCitiesIntro: "We serve all San Bernardino neighborhoods and nearby Inland Empire cities including Rialto, Colton, Highland, Redlands, Fontana, Rancho Cucamonga, Ontario, and Riverside. If you are in San Bernardino County or nearby Riverside County, we have you covered.",
  howItWorksIntro: "Most San Bernardino travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With the Inland Empire being a major hub for international travel and business, San Bernardino travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "San Bernardino and Inland Empire travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in San Bernardino? Here are the most common ones we hear from Inland Empire travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Inland Empire international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Eddie R.",
      location: "San Bernardino, CA",
      rating: 5,
      text: "My family trip to the Philippines was in 3 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Saved our family reunion completely.",
      service: "Emergency Passport",
    },
    {
      name: "Maria G.",
      location: "Rialto, CA",
      rating: 5,
      text: "Needed to renew my passport for a business trip to Japan. The process was incredibly smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Chris P.",
      location: "Redlands, CA",
      rating: 5,
      text: "Lost my passport a week before my cruise to the Bahamas. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Diana L.",
      location: "Fontana, CA",
      rating: 5,
      text: "As a frequent international traveler from the Inland Empire, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "sb-1",
      name: "San Bernardino Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1900 W Redlands Blvd",
      city: "San Bernardino",
      state: "CA",
      zip: "92408",
      phone: "(909) 890-4221",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown San Bernardino",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "sb-2",
      name: "Fontana Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "16615 Arrow Blvd",
      city: "Fontana",
      state: "CA",
      zip: "92335",
      phone: "(909) 822-5714",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from downtown Fontana",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "sb-3",
      name: "Rancho Cucamonga Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10905 East Foothill Blvd",
      city: "Rancho Cucamonga",
      state: "CA",
      zip: "91730",
      phone: "(909) 980-4714",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.6 miles from Victoria Gardens",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for western San Bernardino County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "sb-4",
      name: "Redlands Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "470 N Eureka St",
      city: "Redlands",
      state: "CA",
      zip: "92373",
      phone: "(909) 792-2384",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Redlands",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for eastern San Bernardino County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "sb-5",
      name: "Ontario Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1350 W Holt Blvd",
      city: "Ontario",
      state: "CA",
      zip: "91762",
      phone: "(909) 984-0314",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.7 miles from Ontario Mills",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for southern San Bernardino County residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "sb-6",
      name: "San Bernardino County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "222 W Hospitality Ln",
      city: "San Bernardino",
      state: "CA",
      zip: "92415",
      phone: "(909) 387-8306",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.3 miles from San Bernardino County Government Center",
      website: "https://www.sbcounty.gov/",
      websiteLabel: "SB County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "IE Mountain Coverage", agency: "No IE mountain/foothill expertise", fpc: "San Bernardino Mountains, Route 66 history, and Inland Empire community travel expertise" },
    { feature: "Multi-Person Processing", agency: "Separate appointments per person", fpc: "Handle families and groups in parallel" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Inland Empire photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at San Bernardino County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many San Bernardino applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Inland Empire families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Inland Empire travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in San Bernardino." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your San Bernardino address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in San Bernardino before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in San Bernardino for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Inland Empire travelers." },
    { q: "How fast can I renew my passport in San Bernardino?", a: "Fast Passport Center offers fast passport renewal in San Bernardino in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in San Bernardino in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near San Bernardino?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for San Bernardino residents." },
    { q: "Can I expedite a child passport in San Bernardino?", a: "Yes. Fast Passport Center expedites child passports in San Bernardino for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in San Bernardino with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in San Bernardino?",
      a: "With Fast Passport Center's emergency courier service, San Bernardino travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Los Angeles Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in San Bernardino?",
      a: "Popular options include the San Bernardino Main Post Office (92408), Fontana Post Office (92335), Rancho Cucamonga Post Office (91730), and San Bernardino County Clerk (92415). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in San Bernardino?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Redlands or Fontana?",
      a: "We serve all of San Bernardino County including Redlands, Fontana, Rancho Cucamonga, Ontario, Rialto, Colton, and Highland. Our overnight shipping labels work from any UPS location, so your location within the Inland Empire doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in San Bernardino?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in San Bernardino?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in San Bernardino, Fontana, and Rancho Cucamonga offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in San Bernardino?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in San Bernardino?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Inland Empire families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — San Bernardino", address: "1585 E Highland Ave, San Bernardino, CA 92404", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Fontana", address: "15177 Summit Ave, Fontana, CA 92336", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Rancho Cucamonga", address: "10830 E Foothill Blvd, Rancho Cucamonga, CA 91730", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Redlands", address: "27320 Lugonia Ave, Redlands, CA 92374", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Rialto", address: "1366 S Riverside Ave, Rialto, CA 92376", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Highland", address: "26620 Highland Ave, Highland, CA 92346", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Ontario", address: "4310 E Ontario Mills Pkwy, Ontario, CA 91764", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Rancho Cucamonga", address: "12530 Monte Vista Ave, Rancho Cucamonga, CA 91739", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — San Bernardino",
      address: "4200 E Highland Ave, San Bernardino, CA 92408",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Fontana",
      address: "8625 Nuevo Ave, Fontana, CA 92335",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Rancho Cucamonga",
      address: "9037 Arrow Rte, Rancho Cucamonga, CA 91730",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Redlands",
      address: "1655 W Lugonia Ave, Redlands, CA 92374",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for eastern San Bernardino County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "ONT",
      name: "Ontario International Airport",
      distance: "18 miles from San Bernardino",
      address: "2900 E Airport Dr, Ontario, CA 91761",
      note: "Major commercial airport for the Inland Empire. International flights to Mexico, Canada, and Taiwan.",
      flightUrl: "https://www.flyontario.com/",
      flightLabel: "Ontario Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "60 miles from San Bernardino",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via I-10.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "SBD",
      name: "San Bernardino International Airport",
      distance: "8 miles from downtown San Bernardino",
      address: "105 N Leland Norton Way, San Bernardino, CA 92408",
      note: "Growing commercial airport with cargo operations and limited passenger service. Future expansion planned.",
      flightUrl: "https://www.sbdairport.com/",
      flightLabel: "SBD Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the California Department of Public Health Vital Records for San Bernardino County.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through California CDPH Vital Records.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the San Bernardino County Clerk-Recorder.", url: "https://www.sbcounty.gov/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through San Bernardino County Superior Court.", url: "https://www.sb-court.org/" },
  ],
};